import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useBeforeUnload } from "@/hooks/useBeforeUnload";
import { useCanSeeCost } from "@/hooks/useCanSeeCost";
import { useDebouncedValue } from "@/hooks/use-debounce";
import {
  PublicChecklistData,
  StockChecklistItem,
  ChecklistColumnConfig,
  DEFAULT_COLUMN_CONFIG,
  SKIP_REASON_LABEL,
  stockChecklistPdfTrail,
  type StockCheckSkipReason,
  type StockCheckSkippedItem,
  type StockCheckCadastroAviso,
  type StockCostOrigin,
  type StockCheckAccessMode,
  type StockCheckCountPage,
  type StockCheckPageHeader,
  type StockBalanceRef,
  type ChecklistFilters,
} from "@/types/stockChecklist";
import { StockChecklistTable, EditedItemData } from "@/components/Stock/StockChecklistTable";
import { StockChecklistSummaryDialog, FinalizationStats } from "@/components/Stock/StockChecklistSummaryDialog";
import { StockChecklistPasswordEntry } from "@/components/Stock/StockChecklistPasswordEntry";
import { StockChecklistPasswordPanel } from "@/components/Stock/StockChecklistPasswordPanel";
import { StockChecklistAddProductDialog } from "@/components/Stock/StockChecklistAddProductDialog";
import { FreeCountPanel } from "@/components/Stock/FreeCountPanel";
import { generateStockChecklistPDF } from "@/utils/pdfGenerator";
import type { StockChecklistDocumentProps } from "@/utils/pdfGenerator";
import { todayLocalStr } from "@/utils/dateHelpers";
import { formatCurrency } from "@/utils/formatters";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Package,
  Search,
  Check,
  AlertCircle,
  Loader2,
  Filter,
  CheckCircle2,
  User,
  ArrowLeft,
  ShieldOff,
  Plus,
  ChevronLeft,
  ChevronRight,
  KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// ─── Types ────────────────────────────────────────────────────────────────────

interface RpcResult {
  success: boolean;
  error?: string;
  difference?: number;
  total_adjustments?: number;
  total_checked?: number;
  total_divergences?: number;
  positive_divergences?: number;
  negative_divergences?: number;
  prices_updated?: number;
  photos_updated?: number;
  financial_impact?: number | null; // PROD-CUSTO-RLS (I.167): vazio sem a chave de preços
  checklist_id?: string;
  checklist?: PublicChecklistData["checklist"] & { completed_at?: string | null };
  company?: PublicChecklistData["company"];
  deposit?: PublicChecklistData["deposit"];
  items?: StockChecklistItem[];
  // A7a-i: gravação da contagem
  counted_stock?: number | null;
  system_stock_at_count?: number | null;
  customer_out_at_count?: number | null;
  expected_on_shelf?: number | null;
  registered_units_at_count?: number | null;
  skip_reason?: StockCheckSkipReason | null;
  // A7b: quem contou (save_stock_check_count)
  checked_by_name?: string | null;
  // A8-i: origem do custo, balanço e permissões
  unit_cost?: number | null;
  cost_origin?: StockCostOrigin | null;
  balance?: PublicChecklistData["balance"];
  can_edit_cost?: boolean;
  is_master?: boolean;
  costs_adjusted?: number;
  // A7a-i: finalização
  adjusted?: number;
  skipped?: StockCheckSkippedItem[];
  cadastro_avisos?: StockCheckCadastroAviso[];
}

// A7b: porta da conferência (get_stock_checklist_access)
interface AccessResult {
  success: boolean;
  error?: string;
  mode?: StockCheckAccessMode;
  checklist?: { id: string; name: string; status: string };
  password_required?: boolean;
  blocked?: boolean;
}

const PAGE_SIZE = 100;

const rpcErrorMessage = (err: unknown, fallback: string) =>
  (err as { message?: string } | null)?.message || fallback;

const isOpenStatus = (status: string | undefined) => status === "draft" || status === "in_progress";

// ─── Component ────────────────────────────────────────────────────────────────

export default function StockCheckPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, profile, company, userRole } = useAuth();
  const queryClient = useQueryClient();
  const isMaster = (["master", "master_2"] as string[]).includes(userRole ?? "");

  // A7b: porta (chave / senha / pedir_senha / sem_permissao) decidida pelo banco
  const [access, setAccess] = useState<AccessResult | null>(null);
  const [header, setHeader] = useState<StockCheckPageHeader | null>(null);
  // A7b: página da contagem vinda do banco (decisão 2)
  const [pageData, setPageData] = useState<StockCheckCountPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm.trim(), 300);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [showOnlyUnchecked, setShowOnlyUnchecked] = useState(false);
  const [showOnlyDifference, setShowOnlyDifference] = useState(false);
  const [notCountedSince, setNotCountedSince] = useState("");
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [showEstimate, setShowEstimate] = useState(false); // m-CONF.19
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [savingItemId, setSavingItemId] = useState<string | null>(null);
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [fullData, setFullData] = useState<PublicChecklistData | null>(null);
  const [isLoadingFull, setIsLoadingFull] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [finalizationStats, setFinalizationStats] = useState<FinalizationStats | null>(null);
  const [finalSkipped, setFinalSkipped] = useState<StockCheckSkippedItem[]>([]);
  const [finalAvisos, setFinalAvisos] = useState<StockCheckCadastroAviso[]>([]);
  const pageRequestRef = useRef(0);

  const mode = access?.mode;
  const isKeyMode = mode === "chave";
  const canSeeCost = useCanSeeCost(); // PROD-CUSTO-RLS 15a
  const canCount = mode === "chave" || mode === "senha";

  // Landscape print style for this page only
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "stock-check-print-landscape";
    style.textContent = "@media print { @page { size: A4 landscape; margin: 10mm; } }";
    document.head.appendChild(style);
    return () => { style.remove(); };
  }, []);

  // B8: Warn on tab close when save is in progress
  useBeforeUnload(savingItemId !== null);

  // ── A7b: porta ─────────────────────────────────────────────────────────────
  const loadAccess = useCallback(async () => {
    if (!token) return;
    try {
      setIsLoading(true);
      const { data: result, error: rpcError } = await supabase.rpc("get_stock_checklist_access", { p_token: token });
      if (rpcError) throw rpcError;
      const typed = result as unknown as AccessResult;
      if (!typed.success) {
        setError(typed.error ?? "Erro ao carregar dados");
        return;
      }
      setError(null);
      setAccess(typed);

      // B6: rascunho passa a "em progresso" ao abrir (A7a-i: pelo banco; só com a chave — m-CONF.15 no modo senha)
      if (typed.mode === "chave" && typed.checklist?.status === "draft" && typed.checklist.id) {
        const { error: startError } = await supabase.rpc("set_stock_checklist_status", {
          p_checklist_id: typed.checklist.id,
          p_action:       "start",
        });
        if (startError) console.error("Erro ao iniciar conferência:", startError);
      }
    } catch (err) {
      console.error("Erro ao carregar checklist:", err);
      setError(rpcErrorMessage(err, "Não foi possível carregar o checklist"));
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  // ── CONF-LIVRE (I.181): contagem livre abre o painel próprio (só modo chave) ──
  const [isFreeCount, setIsFreeCount] = useState<boolean | null>(null);
  useEffect(() => {
    if (!token || mode !== "chave") return;
    let cancelled = false;
    (async () => {
      const { data, error: stateError } = await supabase.rpc("get_free_count_state", { p_token: token });
      if (cancelled) return;
      if (stateError) { console.error("Erro ao ler a contagem livre:", stateError); setIsFreeCount(false); return; }
      setIsFreeCount(!!(data as unknown as { free_count?: boolean } | null)?.free_count);
    })();
    return () => { cancelled = true; };
  }, [token, mode]);

  // ── A7b: dados fora da página do banco (colunas escolhidas, balanço, depósito), filtrados pela empresa ──
  useEffect(() => {
    if (!token || !company?.id || !canCount) return;
    let cancelled = false;
    (async () => {
      const { data: row, error: rowError } = await supabase
        .from("stock_checklists")
        .select("created_at, filters, balance_id, deposit:deposits(name)")
        .eq("access_token", token)
        .eq("company_id", company.id)
        .maybeSingle();
      if (rowError || !row) {
        console.error("Erro ao ler a conferência:", rowError);
        return;
      }
      let balance: StockBalanceRef | null = null;
      if (row.balance_id) {
        const { data: bal } = await supabase
          .from("stock_balances")
          .select("id, name, status, counts_valid_since")
          .eq("id", row.balance_id)
          .eq("company_id", company.id)
          .maybeSingle();
        balance = (bal as unknown as StockBalanceRef | null) ?? null;
      }
      if (cancelled) return;
      setHeader({
        created_at:   row.created_at ?? new Date().toISOString(),
        filters:      row.filters as unknown as ChecklistFilters,
        deposit_name: (row as { deposit?: { name: string } | null }).deposit?.name ?? null,
        balance,
      });
    })();
    return () => { cancelled = true; };
  }, [token, company?.id, canCount]);

  // ── A7b: página da contagem (filtros no banco, 100 por página) ─────────────
  useEffect(() => {
    if (!token || !canCount) return;
    const requestId = ++pageRequestRef.current;
    (async () => {
      setIsPageLoading(true);
      const { data: result, error: rpcError } = await supabase.rpc("get_stock_checklist_count_page", {
        p_token:             token,
        p_search:            debouncedSearch || undefined,
        p_category:          categoryFilter === "all" ? undefined : categoryFilter,
        p_only_pending:      showOnlyUnchecked,
        p_only_divergent:    showOnlyDifference,
        // "não contados desde": o dia começa às 00:00 de São Paulo
        p_not_counted_since: notCountedSince ? `${notCountedSince}T00:00:00-03:00` : undefined,
        p_page:              page,
        p_page_size:         PAGE_SIZE,
      });
      if (requestId !== pageRequestRef.current) return;
      setIsPageLoading(false);
      if (rpcError) {
        toast.error(rpcErrorMessage(rpcError, "Erro ao carregar a contagem"));
        // senha trocada ou conferência fechada: volta à porta
        if (mode === "senha") loadAccess();
        return;
      }
      const typed = result as unknown as StockCheckCountPage;
      if (!typed.success) {
        setError(typed.error ?? "Erro ao carregar dados");
        return;
      }
      setPageData(typed);
    })();
  }, [token, canCount, mode, debouncedSearch, categoryFilter, showOnlyUnchecked, showOnlyDifference, notCountedSince, page, reloadKey, loadAccess]);

  // Filtro novo volta à página 1
  useEffect(() => { setPage(1); }, [debouncedSearch, categoryFilter, showOnlyUnchecked, showOnlyDifference, notCountedSince]);

  // Atualiza um item da página e os totais da conferência com a resposta do banco
  const patchItem = (itemId: string, patch: (item: StockChecklistItem) => StockChecklistItem) => {
    setPageData((prev) => {
      if (!prev) return prev;
      const before = prev.items.find((i) => i.id === itemId);
      if (!before) return prev;
      const after = patch(before);
      const isDiv = (i: StockChecklistItem) => i.checked && (i.difference ?? 0) !== 0;
      const counted = prev.totals.counted + (after.checked && !before.checked ? 1 : 0);
      return {
        ...prev,
        totals: {
          ...prev.totals,
          counted,
          pending: prev.totals.items - counted,
          divergent: prev.totals.divergent + (isDiv(after) ? 1 : 0) - (isDiv(before) ? 1 : 0),
        },
        items: prev.items.map((i) => (i.id === itemId ? after : i)),
      };
    });
  };

  // ── Item save ──────────────────────────────────────────────────────────────
  const handleSaveItem = async (itemId: string, itemData: EditedItemData) => {
    if (!token) return;
    try {
      setSavingItemId(itemId);

      // A7b (resposta 2): contagem e observação pela mesma função nos dois modos (chave e senha)
      if (itemData.counted != null || itemData.notes != null) {
        const { data: result, error: rpcError } = await supabase.rpc("save_stock_check_count", {
          p_token:            token,
          p_item_id:          itemId,
          p_counted_quantity: itemData.counted ?? undefined,
          p_notes:            itemData.notes   ?? undefined,
        });
        if (rpcError) throw rpcError;
        const typed = result as unknown as RpcResult;
        if (!typed.success) {
          toast.error(typed.error ?? "Erro ao salvar");
          return;
        }
        patchItem(itemId, (item) => ({
          ...item,
          counted_stock:              typed.counted_stock ?? item.counted_stock,
          difference:                 typed.difference ?? item.difference,
          system_stock_at_count:      typed.system_stock_at_count ?? item.system_stock_at_count,
          customer_out_at_count:      typed.system_stock_at_count != null ? typed.customer_out_at_count ?? null : item.customer_out_at_count,
          registered_units_at_count:  typed.system_stock_at_count != null ? typed.registered_units_at_count ?? null : item.registered_units_at_count,
          expected_on_shelf_at_count: typed.expected_on_shelf ?? item.expected_on_shelf_at_count,
          skip_reason:                typed.system_stock_at_count != null ? typed.skip_reason ?? null : item.skip_reason,
          checked:                    itemData.counted != null ? true : item.checked,
          checked_at:                 itemData.counted != null ? new Date().toISOString() : item.checked_at,
          checked_by_name:            itemData.counted != null ? typed.checked_by_name ?? item.checked_by_name : item.checked_by_name,
          notes:                      itemData.notes ?? item.notes,
        }));
      }

      // Novo P.V., custo e tabelas: só com a chave, pela gravação de sempre (sem a contagem)
      const hasMoney = itemData.salePrice != null || itemData.costPrice != null || itemData.priceTableOverrides != null;
      if (isKeyMode && hasMoney) {
        const { data: result, error: rpcError } = await supabase.rpc("save_stock_check_item_extended", {
          p_token:                 token,
          p_item_id:               itemId,
          p_sale_price:            itemData.salePrice           ?? undefined,
          p_cost_price:            itemData.costPrice           ?? undefined,
          p_price_table_overrides: itemData.priceTableOverrides ?? undefined,
        });
        if (rpcError) throw rpcError;
        const typed = result as unknown as RpcResult;
        if (!typed.success) {
          toast.error(typed.error ?? "Erro ao salvar");
          return;
        }
        patchItem(itemId, (item) => ({
          ...item,
          sale_price:            itemData.salePrice ?? item.sale_price,
          unit_cost:             itemData.costPrice ?? item.unit_cost,
          // A8-i: custo informado vira origem "informado" (resposta do banco)
          cost_origin:           itemData.costPrice != null ? (typed.cost_origin ?? "informado") : item.cost_origin,
          price_edited_by_user:  itemData.salePrice != null
                                   ? itemData.salePrice !== item.system_sale_price
                                   : item.price_edited_by_user,
          cost_edited_by_user:   itemData.costPrice != null
                                   ? itemData.costPrice !== item.system_cost_price
                                   : item.cost_edited_by_user,
          price_table_overrides: itemData.priceTableOverrides
                                   ? { ...item.price_table_overrides, ...itemData.priceTableOverrides }
                                   : item.price_table_overrides,
        }));
      }
    } catch (err) {
      console.error("Erro ao salvar item:", err);
      toast.error(rpcErrorMessage(err, "Erro ao salvar contagem"));
      // senha trocada ou conferência fechada: volta à porta
      if (mode === "senha") loadAccess();
    } finally {
      setSavingItemId(null);
    }
  };

  // ── A8-i: confirmar o custo cadastrado de um item (master, parte de balanço aberto) ──
  const handleConfirmRegisteredCost = async (itemId: string) => {
    if (!token) return;
    try {
      setSavingItemId(itemId);
      const { error: rpcError } = await supabase.rpc("confirm_stock_check_item_registered_cost", {
        p_token:   token,
        p_item_id: itemId,
      });
      if (rpcError) throw rpcError;
      patchItem(itemId, (i) => ({ ...i, cost_origin: "cadastrado_confirmado" }));
      toast.success("Custo cadastrado confirmado");
    } catch (err) {
      console.error("Erro ao confirmar custo:", err);
      toast.error(rpcErrorMessage(err, "Erro ao confirmar custo"));
    } finally {
      setSavingItemId(null);
    }
  };

  // ── Image upload (B3) — só com a chave (resposta 3) ──────────────────────
  const handleUploadImage = async (itemId: string, productId: string, file: File) => {
    if (!company || !user || !token) return;

    try {
      // 1. Upload to storage
      const fileName = `${company.id}/${productId}/${crypto.randomUUID()}.webp`;
      const { error: uploadError } = await supabase.storage
        .from("product-images")
        .upload(fileName, file, { contentType: "image/webp" });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("product-images")
        .getPublicUrl(fileName);

      // 2. Upsert primary image in product_images
      const { data: existing } = await supabase
        .from("product_images")
        .select("id")
        .eq("product_id", productId)
        .eq("company_id", company.id)
        .eq("is_primary", true)
        .maybeSingle();

      if (existing) {
        await supabase
          .from("product_images")
          .update({ image_url: publicUrl })
          .eq("id", existing.id);
      } else {
        await supabase.from("product_images").insert({
          product_id:  productId,
          company_id:  company.id,
          image_url:   publicUrl,
          is_primary:  true,
          created_by:  user.id,
        });
      }

      // 3. Record in checklist item (A7a-i: pelo banco; o navegador não grava mais a conferência direto)
      const { error: photoError } = await supabase.rpc("save_stock_check_item_extended", {
        p_token:     token,
        p_item_id:   itemId,
        p_image_url: publicUrl,
      });
      if (photoError) throw photoError;

      // 4. Update local state
      patchItem(itemId, (i) => ({ ...i, image_url: publicUrl }));

      toast.success("Foto atualizada!");
    } catch (err) {
      console.error("Erro ao enviar foto:", err);
      toast.error("Erro ao enviar foto. Tente novamente.");
    }
  };

  // ── A7b: leitura completa da conferência (resumo da finalização e PDF) ─────
  const loadFullData = async (): Promise<PublicChecklistData | null> => {
    if (!token) return null;
    const { data: result, error: rpcError } = await supabase.rpc("get_stock_checklist_by_token", { p_token: token });
    if (rpcError) throw rpcError;
    const typed = result as unknown as RpcResult;
    if (!typed.success) throw new Error(typed.error ?? "Erro ao carregar dados");
    return {
      checklist: typed.checklist!,
      company:   typed.company!,
      deposit:   typed.deposit,
      balance:   typed.balance ?? null,
      can_edit_cost: typed.can_edit_cost ?? false,
      is_master: typed.is_master ?? false,
      items:     typed.items ?? [],
    };
  };

  const openFinalizeDialog = async () => {
    setShowFinalizeDialog(true);
    setFullData(null);
    try {
      setIsLoadingFull(true);
      setFullData(await loadFullData());
    } catch (err) {
      console.error("Erro ao carregar o resumo:", err);
      toast.error(rpcErrorMessage(err, "Erro ao carregar o resumo da conferência"));
      setShowFinalizeDialog(false);
    } finally {
      setIsLoadingFull(false);
    }
  };

  // ── Finalize ───────────────────────────────────────────────────────────────
  const handleFinalize = async () => {
    if (!token) return;
    try {
      setIsFinalizing(true);

      const { data: result, error: rpcError } = await supabase.rpc(
        "complete_stock_checklist",
        {
          p_token:             token,
          p_completed_by_name: profile?.full_name ?? user?.email ?? null,
        }
      );
      if (rpcError) throw rpcError;

      const typed = result as unknown as RpcResult;
      if (!typed.success) {
        toast.error(typed.error ?? "Erro ao finalizar");
        return;
      }

      setShowFinalizeDialog(false);
      setFinalSkipped(typed.skipped ?? []);
      setFinalAvisos(typed.cadastro_avisos ?? []);
      setFinalizationStats({
        total_checked:       typed.total_checked       ?? 0,
        total_adjustments:   typed.total_adjustments   ?? 0,
        total_divergences:   typed.total_divergences   ?? 0,
        positive_divergences: typed.positive_divergences ?? 0,
        negative_divergences: typed.negative_divergences ?? 0,
        prices_updated:      typed.prices_updated      ?? 0,
        photos_updated:      typed.photos_updated      ?? 0,
        financial_impact:    typed.financial_impact    ?? null, // PROD-CUSTO-RLS 3b: vazio não vira R$ 0,00
      });
      setShowSummary(true);

      queryClient.invalidateQueries({ queryKey: ["stock-checklists"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["stock-summary"] });
    } catch (err) {
      console.error("Erro ao finalizar:", err);
      toast.error(rpcErrorMessage(err, "Erro ao finalizar conferência. Tente novamente."));
    } finally {
      setIsFinalizing(false);
    }
  };

  // ── Generate PDF (A7b: leitura nova depois de finalizar, com o m-CONF.5) ──
  const handleSummaryPdf = async () => {
    if (!finalizationStats) return;
    let data: PublicChecklistData | null;
    try {
      data = await loadFullData();
    } catch (err) {
      toast.error(rpcErrorMessage(err, "Erro ao ler a conferência para o PDF"));
      return;
    }
    if (!data) return;
    const checkedItems = data.items.filter((i) => i.checked);
    // A7a-i: item não acertado não entra na divergência lançada
    const skippedReason = new Map(finalSkipped.map((s) => [s.product_id, s.reason]));
    const divergentItems = checkedItems.filter(
      (i) => i.difference != null && i.difference !== 0 && !skippedReason.has(i.product_id)
    );
    const positiveItems = divergentItems.filter((i) => (i.difference ?? 0) > 0);
    const negativeItems = divergentItems.filter((i) => (i.difference ?? 0) < 0);

    const filters = data.checklist.filters;
    const filterParts: string[] = [];
    if (filters?.categories?.length) filterParts.push(`Categorias: ${filters.categories.join(', ')}`);
    if ((filters as { abc_curves?: string[] } | undefined)?.abc_curves?.length)
      filterParts.push(`Curva ABC: ${((filters as { abc_curves?: string[] }).abc_curves ?? []).join(', ')}`);
    if ((filters as { letterFilter?: string[] } | undefined)?.letterFilter?.length)
      filterParts.push(`Letra: ${((filters as { letterFilter?: string[] }).letterFilter ?? []).join(', ')}`);

    const hasPriceEdits = checkedItems.some((i) => i.price_edited_by_user);
    const hasCostEdits  = checkedItems.some((i) => i.cost_edited_by_user);
    const completedAt = (data.checklist as { completed_at?: string | null }).completed_at;

    const docData: StockChecklistDocumentProps = {
      company: {
        name:     data.company.name,
        logo_url: data.company.logo_url ?? undefined,
      },
      checklist: {
        name:              data.checklist.name,
        created_at:        data.checklist.created_at,
        completed_at:      completedAt ?? new Date().toISOString(),
        deposit_name:      data.deposit?.name ?? null,
        completed_by_name: profile?.full_name ?? user?.email ?? null,
        filters_summary:   filterParts.length ? filterParts.join(' | ') : null,
      },
      stats: {
        total:          data.items.length,
        checked:        checkedItems.length,
        divergent:      divergentItems.length,
        positiveCount:  positiveItems.length,
        negativeCount:  negativeItems.length,
        positiveQty:    positiveItems.reduce((s, i) => s + (i.difference ?? 0), 0),
        negativeQty:    Math.abs(negativeItems.reduce((s, i) => s + (i.difference ?? 0), 0)),
        pricesEdited:   finalizationStats.prices_updated,
        photos:         finalizationStats.photos_updated,
        financialImpact: finalizationStats.financial_impact ?? 0, // vazio: o PDF não imprime o bloco (só mostra acima de 0)
      },
      items: checkedItems.map((i) => ({
        sku:                  i.sku,
        name:                 i.name,
        category:             i.category,
        unit_name:            i.unit_name,
        current_stock:        i.system_stock_at_count ?? i.current_stock,
        counted_stock:        i.counted_stock,
        difference:           i.difference,
        notes:                i.notes,
        sale_price:           i.sale_price,
        system_sale_price:    i.system_sale_price,
        unit_cost:            i.unit_cost,
        system_cost_price:    i.system_cost_price,
        price_edited_by_user: i.price_edited_by_user ?? false,
        cost_edited_by_user:  i.cost_edited_by_user  ?? false,
        skip_label:           skippedReason.has(i.product_id) ? SKIP_REASON_LABEL[skippedReason.get(i.product_id)!] : null,
        ...stockChecklistPdfTrail(i),
      })),
      showSalePriceColumn: hasPriceEdits,
      showCostPriceColumn: hasCostEdits,
      showSkipColumn:      finalSkipped.length > 0,
      showCountedByColumn: checkedItems.some((i) => !!i.checked_by_name),
      showCostTrailColumns: checkedItems.some((i) => !!i.cost_origin),
    };

    const blob    = await generateStockChecklistPDF(docData);
    const url     = URL.createObjectURL(blob);
    const a       = document.createElement('a');
    const safeName = data.checklist.name.replace(/[^a-z0-9]/gi, '-').toLowerCase();
    const dateStr  = todayLocalStr(); // m-CONF.18: dia de São Paulo, não UTC
    a.href        = url;
    a.download    = `conferencia-${safeName}-${dateStr}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('PDF baixado!');
  };

  // ── A7b: item achado — a contagem abre buscando o produto adicionado ──────
  const handleProductAdded = (product: { name: string; sku: string | null }) => {
    setCategoryFilter("all");
    setShowOnlyUnchecked(false);
    setShowOnlyDifference(false);
    setNotCountedSince("");
    setSearchTerm(product.sku || product.name);
    setPage(1);
    setReloadKey((k) => k + 1);
  };

  // ── Derived data ───────────────────────────────────────────────────────────
  const visibleColumns: ChecklistColumnConfig = useMemo(() => {
    const chosen = header?.filters?.visible_columns ?? DEFAULT_COLUMN_CONFIG;
    // A7b (decisão 1): no modo senha não há preço, custo nem tabela; a foto fica só para ver
    // PROD-CUSTO-RLS 15a (resposta 10a): no modo chave, o R$ do custo só para master ou a chave de preços
    if (!isKeyMode) return { ...chosen, sale_price: false, cost_price: false, price_tables: [] };
    return canSeeCost ? chosen : { ...chosen, cost_price: false };
  }, [header?.filters, isKeyMode, canSeeCost]);

  const categories = useMemo(
    () => (pageData?.categories ?? []).map((c) => c.category).filter((c): c is string => !!c),
    [pageData?.categories]
  );

  const totals = pageData?.totals ?? { items: 0, counted: 0, pending: 0, divergent: 0 };
  const progressPercent = totals.items ? Math.round((totals.counted / totals.items) * 100) : 0;
  const totalPages = Math.max(1, Math.ceil((pageData?.filtered_total ?? 0) / (pageData?.page_size ?? PAGE_SIZE)));
  const checklistOpen = isOpenStatus(pageData?.checklist.status);

  // Summary for finalize dialog (B5) — A7b: da leitura completa, porque a página só tem 100 itens
  const finalizeSummary = useMemo(() => {
    if (!fullData?.items) return { counted: 0, items: 0, divergent: 0, skipped: 0, pricesEdited: 0, photos: 0 };
    const checked = fullData.items.filter((i) => i.checked);
    return {
      // m-CONF.20: conferidos e total da mesma leitura completa dos demais números (não dos totais da página)
      counted:      checked.length,
      items:        fullData.items.length,
      divergent:    checked.filter((i) => i.difference !== 0 && i.difference != null && !i.skip_reason).length,
      skipped:      checked.filter((i) => !!i.skip_reason).length,
      pricesEdited: checked.filter((i) => i.price_edited_by_user || i.cost_edited_by_user).length,
      photos:       checked.filter((i) => !!i.image_url).length,
    };
  }, [fullData?.items]);

  // m-CONF.19: impacto estimado da conferência aberta (regra de custo da finalização, no banco), só para quem vê dinheiro
  // na conferência e com o botão ligado (desligado por padrão, Diretor 19/10); relido quando a contagem muda
  // PROD-CUSTO-RLS 15a (resposta 10a): ver o R$ na conferência passa a ser a chave de preços ou master (antes, quem
  // podia editar custo); editar custo não muda
  const canSeeMoney = isKeyMode && canSeeCost;
  const { data: estimatedImpact, isFetching: isEstimating } = useQuery({
    queryKey: ["stock-check-estimate", token, totals.counted, totals.divergent, reloadKey],
    enabled: showEstimate && canSeeMoney && checklistOpen && !!token,
    queryFn: async () => {
      const { data, error: rpcError } = await supabase.rpc("get_stock_checklist_count_page", {
        p_token:     token!,
        p_page_size: 1,
      });
      if (rpcError) throw rpcError;
      return Number((data as unknown as { estimated_impact?: number | null } | null)?.estimated_impact ?? 0);
    },
  });

  // ── Early returns ──────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Não foi possível carregar</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading && !access) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando conferência...</p>
        </div>
      </div>
    );
  }

  if (!access) return null;

  // A7b: conferência sem senha para quem não tem a chave (evidência d)
  if (mode === "sem_permissao") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <ShieldOff className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Sem Permissão</h2>
            <p className="text-muted-foreground mb-6">
              Você não tem permissão para acessar conferências de estoque.
              Solicite acesso ao administrador da sua empresa.
            </p>
            <Button variant="outline" onClick={() => navigate("/")}>Voltar ao início</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // A7b: conferência com senha, usuário ainda sem entrada válida
  if (mode === "pedir_senha" && token) {
    return (
      <StockChecklistPasswordEntry
        key={`${access.checklist?.id}-${access.blocked}`}
        token={token}
        checklistName={access.checklist?.name ?? ""}
        blocked={access.blocked ?? false}
        onEntered={() => { setPageData(null); loadAccess(); }}
      />
    );
  }

  // CONF-LIVRE (I.181): contagem livre (modo chave) usa o painel próprio, aberta ou fechada
  if (mode === "chave" && isFreeCount && token) {
    return <FreeCountPanel token={token} />;
  }
  // Enquanto não se sabe se é contagem livre, não mostra a página comum (ela tem o saldo do sistema)
  if (mode === "chave" && isFreeCount === null && token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando conferência...</p>
        </div>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Conferência Finalizada!</h2>
            <p className="text-muted-foreground mb-4">
              Todos os ajustes foram aplicados ao estoque com sucesso.
            </p>
            <p className="text-sm text-muted-foreground">Você pode fechar esta página.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando conferência...</p>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-card border-b border-border shadow-sm no-print">
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            {/* m-CONF.1: volta à lista de conferências (contagens em espera são gravadas ao sair) */}
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/stock-checklist")}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Voltar à lista
            </Button>
            {company?.logo_url && (
              <img src={company.logo_url} alt="Logo" className="h-10 w-auto object-contain" />
            )}
            <div className="flex-1 min-w-0">
              <h1 className="font-semibold text-foreground truncate">{pageData.checklist.name}</h1>
              <p className="text-sm text-muted-foreground">
                {company?.name}{header?.deposit_name ? ` · Depósito ${header.deposit_name}` : ""}
              </p>
            </div>
            {mode === "senha" && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-xs text-muted-foreground">
                <KeyRound className="h-3 w-3" /> Entrada pela senha
              </div>
            )}
            {profile && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 rounded-full">
                <User className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400 truncate max-w-[120px]">
                  {profile.full_name ?? user?.email?.split("@")[0]}
                </span>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Progresso · {totals.pending} pendentes · {totals.divergent} divergentes
              </span>
              <span className="font-medium">{totals.counted} / {totals.items}</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>
          {canSeeMoney && checklistOpen && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <Switch id="mostrar-impacto-estimado" checked={showEstimate} onCheckedChange={setShowEstimate} />
              <Label htmlFor="mostrar-impacto-estimado" className="font-normal">Mostrar impacto estimado</Label>
              {showEstimate && (
                <span className="text-muted-foreground">
                  {isEstimating && estimatedImpact === undefined ? (
                    "Calculando..."
                  ) : (
                    <>
                      <span className="font-medium text-foreground">
                        {formatCurrency(estimatedImpact ?? 0)}
                      </span>{" "}
                      (estimativa pela regra de custo da finalização; só itens com diferença que serão acertados)
                    </>
                  )}
                </span>
              )}
            </div>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Acerto = contado − esperado na hora da contagem (saldo do depósito menos o que está com cliente sem baixa).
            Vendas e movimentos depois da contagem são preservados; o acerto é lançado ao finalizar.
          </p>
          {header?.balance && (
            <p className="mt-2 text-xs font-medium text-primary">
              Parte do balanço "{header.balance.name}"
              {header.balance.status === "open" ? "" : " (balanço encerrado)"}
              {" · "}na finalização, o custo informado, o cadastrado confirmado ou o último custo do momento de cada item
              contado vira o custo médio antes do acerto (cadastrado sem confirmar não vale).
            </p>
          )}
          {/* A7b: senha da conferência para quem tem a chave */}
          {isKeyMode && checklistOpen && token && (
            <StockChecklistPasswordPanel
              token={token}
              passwordRequired={pageData.checklist.password_required}
              canTurnOn={totals.counted === 0}
              onChanged={() => setReloadKey((k) => k + 1)}
            />
          )}
        </div>

        {/* Search and filters — A7b: no banco */}
        <div className="px-4 pb-4 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome, SKU ou código de barras..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            {checklistOpen && (
              <Button variant="outline" onClick={() => setShowAddProduct(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Adicionar produto
              </Button>
            )}
          </div>

          <div className="flex gap-2 flex-wrap items-center">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[160px] h-9">
                <Filter className="h-3 w-3 mr-1" />
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant={showOnlyUnchecked ? "default" : "outline"}
              size="sm"
              onClick={() => setShowOnlyUnchecked(!showOnlyUnchecked)}
              className="h-9"
            >
              Pendentes
            </Button>

            <Button
              variant={showOnlyDifference ? "default" : "outline"}
              size="sm"
              onClick={() => setShowOnlyDifference(!showOnlyDifference)}
              className="h-9"
            >
              Divergentes
            </Button>

            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              Não contados desde
              <Input
                type="date"
                value={notCountedSince}
                onChange={(e) => setNotCountedSince(e.target.value)}
                className="h-9 w-[150px]"
              />
            </label>
            {isPageLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="p-4 space-y-3">
        {pageData.items.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">Nenhum produto encontrado</p>
            </CardContent>
          </Card>
        ) : (
          <StockChecklistTable
            items={pageData.items}
            visibleColumns={visibleColumns}
            onSaveItem={handleSaveItem}
            onUploadImage={isKeyMode && visibleColumns.photo ? handleUploadImage : undefined}
            savingItemId={savingItemId}
            canEditCost={isKeyMode && pageData.can_edit_cost}
            canEditPrice={isKeyMode && pageData.can_edit_price}
            onConfirmRegisteredCost={
              isKeyMode && isMaster && header?.balance?.status === "open" ? handleConfirmRegisteredCost : undefined
            }
          />
        )}

        {/* A7b: páginas de 100 */}
        <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
          <span>
            {pageData.filtered_total} produto(s) · página {pageData.page} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || isPageLoading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages || isPageLoading} onClick={() => setPage((p) => p + 1)}>
              Próxima <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4 safe-area-inset-bottom no-print">
        {isKeyMode ? (
          <Button
            className="w-full"
            size="lg"
            // A4: also disabled when save in progress
            disabled={totals.counted === 0 || savingItemId !== null}
            onClick={openFinalizeDialog}
            title={savingItemId !== null ? "Salvamento em andamento, aguarde..." : undefined}
          >
            <Check className="mr-2 h-4 w-4" />
            {savingItemId !== null ? "Salvando..." : "Finalizar Conferência"}
          </Button>
        ) : (
          // A7b (evidência c): quem entrou pela senha conta, mas não finaliza
          <p className="text-center text-sm text-muted-foreground">
            Finalizar pede a chave de conferência (quem criou a conferência ou o responsável pelo estoque).
          </p>
        )}
      </div>

      {/* B5: AlertDialog for finalization */}
      <AlertDialog
        open={showFinalizeDialog}
        onOpenChange={(open) => !isFinalizing && setShowFinalizeDialog(open)}
      >
        <AlertDialogContent className="sm:max-w-md no-print">
          <AlertDialogHeader>
            <AlertDialogTitle>Finalizar Conferência</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Os ajustes de estoque serão aplicados automaticamente.{" "}
                  <strong className="text-destructive">Esta ação não pode ser desfeita.</strong>
                </p>
                {isLoadingFull || !fullData ? (
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-lg text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" /> Carregando o resumo...
                  </div>
                ) : (
                  <div className="p-3 bg-muted rounded-lg space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Itens conferidos</span>
                      <span className="font-medium">{finalizeSummary.counted} de {finalizeSummary.items}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Com divergência</span>
                      <span className="font-medium">{finalizeSummary.divergent}</span>
                    </div>
                    {finalizeSummary.skipped > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Não serão acertados</span>
                        <span className="font-medium text-amber-600">{finalizeSummary.skipped}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Preços a atualizar</span>
                      <span className="font-medium">{finalizeSummary.pricesEdited}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Fotos inseridas</span>
                      <span className="font-medium">{finalizeSummary.photos}</span>
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isFinalizing}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleFinalize}
              disabled={isFinalizing || isLoadingFull || !fullData}
              className="bg-primary hover:bg-primary/90"
            >
              {isFinalizing ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />Finalizando...</>
              ) : (
                <><Check className="h-4 w-4 mr-2" />Confirmar e Finalizar</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* A7b: item achado na prateleira */}
      {token && (
        <StockChecklistAddProductDialog
          token={token}
          open={showAddProduct}
          onOpenChange={setShowAddProduct}
          onAdded={handleProductAdded}
        />
      )}

      {/* F7: Summary dialog after finalization */}
      {showSummary && finalizationStats && (
        <StockChecklistSummaryDialog
          open={showSummary}
          stats={finalizationStats}
          checklistName={pageData.checklist.name}
          skipped={finalSkipped}
          cadastroAvisos={finalAvisos}
          onDownloadPdf={handleSummaryPdf}
          onClose={() => {
            setShowSummary(false);
            setIsCompleted(true);
          }}
        />
      )}
    </div>
  );
}
